/**
 * ⭐ Le NOM de celui qui s'est réservé une discussion, ou qui l'a dans sa file.
 *
 * La liste Cyrano ne connaît que le `workos_user_id` de l'épingleur
 * (`epinglee_par`) et du tenant de la file (`tenue_par`) : le backend n'a pas
 * les pseudos — sa session porte un e-mail, et Kayla ne veut jamais lire un
 * e-mail. Les pseudos vivent dans Convex, et c'est ici qu'on fait la jointure,
 * une fois, pour toute la liste.
 *
 * 💸 On ne demande QUE les pseudos des chatteurs que la liste nomme. Ce hook
 * s'abonnait à `accountMembers.listForAccount` — l'équipe ENTIÈRE, rôles,
 * droits et périmètres compris : 436 Ko lus à chaque relance, sur l'écran que
 * TOUS les chatteurs gardent ouvert (mesuré le 09-10-2026, 1er poste de la
 * facture Convex). `operators.getManyByWorkosIds` ne lit que les fiches
 * nommées : une poignée.
 *
 * Rend une `Map<workosUserId, pseudo>`. Vide tant que Convex n'a pas répondu
 * ou hors de toute org : la ligne écrit alors « un autre chatteur », jamais
 * un identifiant.
 */
import { useMemo } from "react";
import { useAtomValue } from "jotai";
import { useQuery } from "convex/react";

import { api } from "@/../convex/_generated/api";
import { conversationsAtom, hydratedConversationsAtom } from "./atoms";

export function useNomsDesChatteurs(): ReadonlyMap<string, string> {
  const conversations = useAtomValue(conversationsAtom);
  const hydratees = useAtomValue(hydratedConversationsAtom);
  const workosUserIds = useMemo(() => {
    const ids = new Set<string>();
    for (const c of [...conversations, ...Object.values(hydratees)]) {
      if (c.epinglee_par) ids.add(c.epinglee_par);
      if (c.tenue_par) ids.add(c.tenue_par);
    }
    // Trié : les mêmes noms rendent les mêmes arguments, donc le même
    // abonnement — l'ordre des cartes bouge, pas la question.
    return [...ids].sort();
  }, [conversations, hydratees]);
  const fiches = useQuery(
    api.operators.getManyByWorkosIds,
    workosUserIds.length > 0 ? { workosUserIds } : "skip",
  );
  return useMemo(() => {
    const noms = new Map<string, string>();
    for (const f of fiches ?? []) {
      if (f.name) noms.set(f.workosUserId, f.name);
    }
    return noms;
  }, [fiches]);
}
